// Client-side port of lease_render/render_lease.py for the live preview iframe.
// Drift versus the server-side WeasyPrint PDF is accepted; only the PDF (which
// the tenant actually signs) goes through the Python renderer via /api/lease/pdf.

import { marked } from 'marked';
import { getLeaseCss, readComponent } from './leaseContent';
// Bundle the cover photo + brand lockup + brass monogram so the preview iframe
// can load them via hashed /assets/ URLs. The Python renderer resolves these
// `assets/...` paths relative to lease_render/; these imports are preview-only.
// The lease cover brand mark is the LOCKUP (wordmark + monogram), matching
// render_lease.py's cover; the addendum's agreement cover rail carries the
// boxed brass monogram, matching the leaseholder agreement covers.
import coverPhotoUrl from '../../../../lease_render/assets/cover-photo.png';
import lockupUrl from '../../../../lease_render/assets/lockup.svg?url';
import monogramBrassUrl from '../../../../lease_render/assets/whp-monogram-brass.png';
// Utility-provider logos: bundled (via Vite) so the preview iframe resolves
// them. The PDF (Python) loads the same files from `assets/` directly, so the
// rendered markup uses the `assets/...` path and we rewrite it to the bundled
// URL for the preview only (mirrors the monogram approach). `?url` forces an
// asset-URL import regardless of any SVG-as-component plugin.
import logoPacificPowerUrl from '../../../../lease_render/assets/logo-pacific-power.svg?url';
import logoColumbiaReaUrl from '../../../../lease_render/assets/logo-columbia-rea.svg?url';
import logoCascadeGasUrl from '../../../../lease_render/assets/logo-cascade-natural-gas.png';
import logoBdiUrl from '../../../../lease_render/assets/logo-bdi.svg?url';

const PREVIEW_ASSET_URLS: Record<string, string> = {
  'assets/cover-photo.png': coverPhotoUrl,
  'assets/lockup.svg': lockupUrl,
  'assets/whp-monogram-brass.png': monogramBrassUrl,
  'assets/logo-pacific-power.svg': logoPacificPowerUrl,
  'assets/logo-columbia-rea.svg': logoColumbiaReaUrl,
  'assets/logo-cascade-natural-gas.png': logoCascadeGasUrl,
  'assets/logo-bdi.svg': logoBdiUrl,
};

/** Rewrite `assets/...` logo paths to their bundled URLs for the preview. */
function bundlePreviewAssets(html: string): string {
  let out = html;
  for (const [path, url] of Object.entries(PREVIEW_ASSET_URLS)) {
    out = out.split(`src="${path}"`).join(`src="${url}"`);
  }
  return out;
}

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
const SOURCE_COMMENT_RE = /<!--[\s\S]*?-->\s*/g;
// B2 (Codex): a DocuSeal field tag looks like `{{Name;type=signature;role=X}}`.
// Reject UNTRUSTED prefill VARIABLE VALUES matching this pattern so staff-
// entered values can't inject a real signer/field. We do NOT apply this blanket
// to every render variable: some SERVER-BUILT variables (e.g.
// approved_animals_table) legitimately carry the renderer's own signer tags.
// Only the UNTRUSTED_PREFILL_KEYS set (free-text staff input) is guarded.
// Not global — used only with .test() on individual values.
const DOCUSEAL_TAG_RE = /\{\{[^}]*;type=/;
// Render `variables` keys whose values come from STAFF FREE-TEXT (lease form /
// addendum prefill). Mirrors UNTRUSTED_PREFILL_KEYS in render_lease.py.
const UNTRUSTED_PREFILL_KEYS = [
  'resident_names',
  'premises_address',
  'assigned_parking',
  'parking_notes',
  // Lease OTHER TERMS is server-wrapped but embeds untrusted staff text.
  'other_terms_section',
  // RRA v2.2 part B pending-decision clause inlines the marked animals' NAMES
  // (household / staff typed) — guard it. Mirrors render_lease.py.
  'animal_decision_pending_paragraph',
  // RESIDENT DESIGNATION intro inlines resident_names/premises_address (untrusted).
  'resident_designation_intro',
  // Move-In Payment Plan / SUMMARY / MOVE-IN FEES inline staff fee names.
  'amounts_due_sections',
  'payment_plan_inclusions',
  'move_in_fees_body',
  // Adoption addendum still uses the raw key.
  'other_terms',
  'current_residents',
  'new_residents',
  'premises',
  'start_date',
  'end_date',
  // Move-Out Funds Direction Agreement prefill (WEL-48) — staff/intake free-text
  // baked into the bespoke document; guard each against field-tag injection.
  // Mirrors UNTRUSTED_PREFILL_KEYS in render_lease.py. (bound_parties_and/_or are
  // server-generated fixed phrasing — trusted; cosigners_recital carries co-signer
  // NAMES, so it IS guarded.)
  'agreement_date',
  'residents',
  'cosigners_recital',
  'designated_recipient_name',
  'lease_date',
  'designated_recipient_address',
] as const;

interface UtilityStateDef {
  template: string;
  label: string;
  billing: string;
  share: string | null;
}

const UTILITY_STATES: Record<string, UtilityStateDef> = {
  tenant_direct: {
    template: 'utility_state_tenant_direct.md',
    label: 'Resident direct account',
    billing: 'Resident establishes account with provider',
    share: '100%',
  },
  landlord_billed_full: {
    template: 'utility_state_landlord_billed_full.md',
    label: 'Lessor account, billed to Resident',
    billing: 'Lessor pays provider and bills Resident',
    share: '100%',
  },
  landlord_provided: {
    template: 'utility_state_landlord_provided.md',
    label: 'Lessor provided',
    billing: 'Included with tenancy',
    share: 'No separate charge',
  },
  ratio: {
    template: 'utility_state_ratio.md',
    label: 'Allocated share',
    billing: 'Lessor bills allocated share',
    share: null,
  },
};

const UNNUMBERED_MAIN_HEADINGS = new Set([
  'AGREEMENT BETWEEN LANDLORD AND TENANT',
  'RESIDENT DESIGNATION',
  'SIGNATURES OF ALL PARTIES',
]);

const CHARGE_HEADINGS = new Set([
  'RENT',
  'CARPET',
  'SECURITY / DAMAGE / CLEANING DEPOSIT',
  'TERMINATION OF TENANCIES',
  'APPLICATIONS AND SCREENING FEES',
  'ANIMALS',
  'APPROVED ANIMALS',
  'LOCKS & KEYS',
  'SMOKE DETECTION DEVICES / FIRE SAFETY AND PROTECTION INFORMATION',
  'GUESTS',
  'UTILITY CHARGES',
  "FEES, ATTORNEY'S FEES / VENUE AND JURISDICTION",
  'RENTERS INSURANCE',
  'ENTRY / INSPECTIONS / NOTICE',
  'SUMMARY OF FUNDS RECEIVED AND DUE',
  'YARD CARE',
  // Renamed from SECURITY DEPOSIT PAYMENT PLAN (2026-10-08).
  'MOVE-IN PAYMENT PLAN',
  'MOVE-IN FEES',
]);

const TITLE_OVERRIDES: Record<string, string> = {
  HOA: 'HOA',
  SFR: 'SFR',
  HVAC: 'HVAC',
  NO: 'No',
};

// The boxed WHP monogram (frame + W), viewBox 0 0 1280 769, fill=currentColor.
// Byte-for-byte copy of lease_render/brand_assets.py MONOGRAM_MARK_SVG so the
// standalone masthead is parity-identical to the Python renderer. Inlined (not
// <img>) so it inherits currentColor.
const MONOGRAM_MARK_SVG =
  '<svg viewBox="0 0 1280 769" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g fill="currentColor"><path d="M779.36317,523.86366H500.63683V245.13634H779.36317ZM507.711,516.78954H772.289V252.21046H507.711Z"></path><path d="M545.49814,360.28808q-1.90533-4.96162-3.05889-7.96349a18.71443,18.71443,0,0,0-2.416-4.64538,6.54829,6.54829,0,0,0-2.80485-2.22936,14.54674,14.54674,0,0,0-4.20468-.7051l-3.6966-.24886-.38365-.76731h35.80461l-.37848.76731-4.08025.12443a24.66545,24.66545,0,0,0-3.95064.44587,2.60927,2.60927,0,0,0-1.90792,1.39984,4.82744,4.82744,0,0,0-.0674,3.05889q.45106,1.97532,1.59685,5.41269l19.36435,54.66606h.254l17.58605-44.08955-1.9131-5.226q-1.91312-4.96162-3.18333-7.96349a22.20309,22.20309,0,0,0-2.48859-4.58316,6.64944,6.64944,0,0,0-2.79966-2.22936,13.71485,13.71485,0,0,0-4.27209-.76732l-3.69141-.24886-.38365-.76731H609.9734l-.38365.76731-3.821.12443a22.37732,22.37732,0,0,0-4.27208.50809,2.4563,2.4563,0,0,0-1.84571,1.39983,5.22708,5.22708,0,0,0,.19183,3.05889q.57549,2.03754,1.72128,5.35048l19.87762,54.40683h.25405l16.181-44.214a96.086,96.086,0,0,0,3.25072-9.93363,12.63949,12.63949,0,0,0,.37847-5.99335,4.43076,4.43076,0,0,0-3.05889-3.0589,32.96949,32.96949,0,0,0-7.07175-1.39983l-2.67524-.24886-.37847-.76731h28.66547l-.37848.76731-2.04272.24886a17.0427,17.0427,0,0,0-4.32911.954,8.00024,8.00024,0,0,0-3.38034,2.93446,36.011,36.011,0,0,0-3.37515,6.30444q-1.78089,4.09062-4.32911,10.96017l-22.04477,59.37364H615.832l-21.78554-58.22267h-.12962l-23.06095,58.22267h-1.2754Z"></path><path d="M661.95868,424.48573l4.693-.52026a17.46858,17.46858,0,0,0,4.831-.84941c.95558-.39285,1.43337-.79632,1.43337-1.23164V346.73334q0-.653-1.24225-1.05113a39.43306,39.43306,0,0,0-6.32276-.77509l-3.2649-.39285-.39285-.78569h35.48919l-.39285.78569-5.21853.52026a17.38021,17.38021,0,0,0-4.82568.84941c-.95558.39285-1.43337.80693-1.43337,1.23164v36.01475h42.00837v-36.397c0-.43532-.41408-.7857-1.23694-1.05113a39.63482,39.63482,0,0,0-6.32807-.77509l-3.78516-.39285-.39285-.78569h35.48919l-.39285.78569-4.69827.52026a17.38021,17.38021,0,0,0-4.82568.84941c-.95558.39285-1.43337.80693-1.43337,1.23164v75.15108c0,.43532.40878.7857,1.23695,1.05113a39.63462,39.63462,0,0,0,6.32806.77509l3.2649.39285.38754.78569H715.44994l.38754-.78569,5.21853-.52026a17.46858,17.46858,0,0,0,4.831-.84941c.95558-.39285,1.43337-.79632,1.43337-1.23164V384.43629H685.312v37.83037c0,.43532.40878.7857,1.23695,1.05113a39.63462,39.63462,0,0,0,6.32806.77509l3.78516.39285.38754.78569H661.56583Z"></path></g></svg>';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

// Brand masthead for a standalone signable document: the WHP monogram, the
// wordmark, then the document's OWN title — centered above the body. Byte-for-byte
// mirror of render_lease.py render_standalone_masthead (a missing monogram degrades
// to wordmark + title; that branch can't fire here since the SVG is always present,
// but the guard is kept for parity).
function renderStandaloneMasthead(title: string): string {
  const mark = MONOGRAM_MARK_SVG || '';
  const markHtml = mark ? `<div class="doc-mark">${mark}</div>\n  ` : '';
  return (
    '<header class="doc-masthead">\n  ' +
    markHtml +
    '<div class="doc-wordmark">Welcome Home Properties</div>\n' +
    `  <h1 class="doc-title">${escapeHtml(String(title))}</h1>\n` +
    '</header>'
  );
}

function stripSourceComments(text: string): string {
  return text.replace(SOURCE_COMMENT_RE, '');
}

function removeGeneratedTocNote(text: string): string {
  return text.replace(
    /\n*Note: All sections or exhibits mentioning specific up-front or potential charges during the lease term should be marked in the generated table of contents\.\n*/g,
    '\n\n',
  );
}

function demoteH2AfterFirst(text: string): string {
  let seen = false;
  const out: string[] = [];
  for (const line of text.split('\n')) {
    if (line.startsWith('## ')) {
      if (seen) {
        out.push('#' + line);
      } else {
        seen = true;
        out.push(line);
      }
      continue;
    }
    out.push(line);
  }
  return out.join('\n');
}

/**
 * B2 (Codex): raise if any UNTRUSTED prefill variable value contains a DocuSeal
 * field tag. Called once per render on the full variables block. Server-built
 * variables (tables, pre-rendered sections) are intentionally not checked —
 * they may legitimately carry the renderer's own signer tags.
 */
function assertNoPrefillTagInjection(variables: Record<string, unknown>): void {
  for (const key of UNTRUSTED_PREFILL_KEYS) {
    const value = variables[key];
    if (typeof value === 'string' && DOCUSEAL_TAG_RE.test(value)) {
      throw new Error(
        `Prefill variable ${JSON.stringify(key)} contains a DocuSeal field tag ` +
          `(${JSON.stringify(value)}); refusing to render to prevent field injection.`,
      );
    }
  }
}

function replaceVariables(text: string, variables: Record<string, unknown>): string {
  return text.replace(PLACEHOLDER_RE, (match, key: string) => {
    if (!(key in variables)) return match;
    return String(variables[key]);
  });
}

// RRA v2.2 part B (WEL-180): the pending-decision clause. The $0 math ships in
// buildRenderConfig while the slot that prints the clause lives in the lease content
// (animals_permitted.md), which the preview fetches from the server at runtime and the
// PDF renderer reads from S3 — so the content can lag the code. If it does, a marked
// animal would print at $0 with NO clause and nothing would notice: refuse instead.
// Mirror of assert_pending_decision_slot_placed in render_lease.py.
const PENDING_DECISION_KEY = 'animal_decision_pending_paragraph';

function assertPendingDecisionSlotPlaced(template: string, variables: Record<string, unknown>): void {
  const value = variables[PENDING_DECISION_KEY];
  if (value === undefined || value === null || String(value).trim() === '') return;
  for (const m of template.matchAll(PLACEHOLDER_RE)) {
    if (m[1] === PENDING_DECISION_KEY) return;
  }
  throw new Error(
    'The lease config carries a pending-decision animal clause ' +
      `(${PENDING_DECISION_KEY}) but the lease content has no slot for it: the lease ` +
      'content is older than the API — sync lease content ' +
      '(scripts/sync-lease-content.sh prod, or POST /admin/reload-lease) and retry. ' +
      'Refusing to render: the marked animal(s) would print at $0 with no clause.',
  );
}

function slugForUtility(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

interface UtilityItem {
  name: string;
  state: string;
  share?: string;
  ratio?: string;
  billing?: string;
}

interface RenderConfig {
  variables: Record<string, unknown>;
  utilities: UtilityItem[];
  modules: Record<string, string | undefined>;
  include_utility_contacts?: boolean;
  include_outdoor_space?: boolean;
  include_hoa?: boolean;
  include_sfr_areas_not_included?: boolean;
  /** §4 negotiated no-fee early-termination-by-notice option (staff-enabled). */
  include_early_termination_notice?: boolean;
  /** WEL-89: false drops the (last numbered) CARPET section. Absent → true (unit has carpet). */
  has_carpet?: boolean;
  /** RENEWAL policy: false drops the YARD CARE section (renewal leases). Absent → true. */
  include_yard_care?: boolean;
  include_payment_plan?: boolean;
  include_move_in_fees?: boolean;
  include_other_monthly_charges?: boolean;
  include_animal_rent?: boolean;
  include_lbp?: boolean;
  document_code?: unknown;
  title?: string;
  cover_image?: string;
  watermark_initials?: string;
  is_generated_for_signing?: boolean;
  /**
   * Number of residents on the lease (1..12). Drives the resident-count-
   * aware expansion of `[[RESIDENT_INITIALS]]` and `[[SIGNATURE_BLOCK]]`
   * markers and the per-resident DocuSeal initials/signature tags
   * (`{{IntK;type=initials;role=ResidentK}}` /
   * `{{SigK;type=signature;role=ResidentK}}`). See
   * lease_components/docuseal_signer_field_registry.md.
   */
  resident_count?: number;
  /**
   * Per-resident signer names in Resident{k} order — labels each signature block
   * with the actual name (cosmetic; role/order unchanged). SYNC WITH
   * lease_render/render_lease.py + leaseConfig.ts.
   */
  resident_signers?: string[];
  /**
   * Render-mode selector. Absent or `'lease'` renders the full lease compile
   * order. `'adoption_addendum'` renders ONLY the Lease Adoption Addendum
   * component (short standalone doc, residents + single Landlord signature
   * block, no cover page). `'moveout_funds_direction'` renders ONLY the
   * Move-Out Funds Direction Agreement component (WEL-48), with a parallel
   * signature block built from `signers`. `'payment_plan'` renders ONLY the
   * standalone Security Deposit Payment Plan component (WEL-77), with a
   * residents-then-Lessor signature block built from `signers`. Mirrors
   * `document_type` dispatch in lease_render/render_lease.py's build_html.
   */
  document_type?: string;
  /**
   * Signer roster for the standalone-document render modes — one DocuSeal
   * signature tag is emitted per entry (role drives the DocuSeal role + a unique
   * field name; name labels the cell).
   *
   * Move-Out Funds Direction Agreement (WEL-48): built by
   * buildMoveoutFundsSubmitters in apps/api/src/lib/moveoutFunds.ts; roles are
   * Resident{i} / Cosigner{i} (the Designated Recipient NEVER signs — WEL-143)
   * / Lessor.
   *
   * Security Deposit Payment Plan (WEL-77): built by buildPaymentPlanSubmitters
   * in apps/api/src/lib/paymentPlanDoc.ts; roles are Resident{i} (order 0) then
   * the single Lessor (order 1, countersigns).
   *
   * Consumed by the `moveout_funds_direction` and `payment_plan` render modes.
   * SYNC WITH render_lease.py's render_moveout_funds_signature_block /
   * render_payment_plan_signature_block.
   */
  signers?: MoveoutFundsSigner[];
}

interface MoveoutFundsSigner {
  role: string;
  name: string;
}

function utilityLookup(config: RenderConfig): Record<string, UtilityItem> {
  const out: Record<string, UtilityItem> = {};
  for (const item of config.utilities) {
    out[slugForUtility(item.name)] = item;
  }
  return out;
}

function titleForToc(title: string): string {
  title = title.replace(/^\d+\.\s*/, '').trim();
  const words: string[] = [];
  for (const word of title.replace(/\//g, ' / ').split(/\s+/)) {
    if (!word) continue;
    const upper = word.toUpperCase();
    if (TITLE_OVERRIDES[upper]) {
      words.push(TITLE_OVERRIDES[upper]);
    } else if (word === '/') {
      words.push('/');
    } else {
      words.push(word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase());
    }
  }
  return words.join(' ').replace(/ \/ /g, '/');
}

// meeting-2026-05-13: §2 RENT content is now pre-rendered server-side as
// `rent_table_inline` + `rent_payability_paragraph` variables, with all
// amounts (including per-charge other-monthly rows and the computed total)
// baked in. The renderer just does standard variable substitution. The
// prior `renderRentTable` + payability constants were removed in favor of
// the single source of truth in leaseConfig.ts.

function renderUtilityFramework(config: RenderConfig): string {
  let framework = readComponent('utilities_framework.md');
  const variables: Record<string, unknown> = { ...config.variables };
  const paragraphBlocks: string[] = [];

  for (const item of config.utilities) {
    const state = item.state;
    const stateDef = UTILITY_STATES[state];
    if (!stateDef) throw new Error(`Unsupported utility state: ${state}`);
    const utilitySlug = slugForUtility(item.name);
    const share = item.share ?? stateDef.share ?? item.ratio ?? '';
    variables[`${utilitySlug}_utility_state_label`] = stateDef.label;
    variables[`${utilitySlug}_billing_method`] = item.billing ?? stateDef.billing;
    variables[`${utilitySlug}_resident_share`] = share;

    const stateTemplate = readComponent(stateDef.template);
    const stateVars = {
      utility_name: item.name.toLowerCase(),
      utility_ratio: item.ratio ?? share,
    };
    const rendered = replaceVariables(stateTemplate, stateVars);
    paragraphBlocks.push(`### ${item.name}\n\n${rendered}`);
  }

  framework = framework.replace(
    '[[RENDERED_UTILITY_STATE_PARAGRAPHS]]',
    paragraphBlocks.join('\n\n'),
  );
  return replaceVariables(framework, variables);
}

function renderDirectUtilityInstructions(config: RenderConfig): string {
  if (!config.include_utility_contacts) return '';

  const utilities = utilityLookup(config);
  const directNames: Record<string, string> = {};
  for (const [slug, item] of Object.entries(utilities)) {
    if (item.state === 'tenant_direct') directNames[slug] = item.name;
  }
  if (Object.keys(directNames).length === 0) return '';

  const instructions = readComponent('utility_contacts.md');
  const marker = '[[RENDER_TENANT_DIRECT_UTILITY_INSTRUCTIONS]]';
  const markerIdx = instructions.indexOf(marker);
  if (markerIdx === -1) return instructions.trim();
  const preamble = instructions.slice(0, markerIdx);
  const afterMarker = instructions.slice(markerIdx + marker.length);

  const h3Re = /^###\s+([^\n]+)\n([\s\S]*?)(?=^###\s+|$(?![\r\n]))/gm;
  const sections: Array<{ name: string; body: string }> = [];
  let m: RegExpExecArray | null;
  while ((m = h3Re.exec(afterMarker)) !== null) {
    sections.push({ name: (m[1] ?? '').trim(), body: (m[2] ?? '').replace(/\s+$/, '') });
  }

  const headingToUtility: Record<string, string> = {
    electric: 'electricity',
    gas: 'gas',
    garbage: 'garbage',
    water: 'water',
    sewer: 'sewer',
  };

  const directLower = new Set(Object.values(directNames).map((n) => n.toLowerCase()));
  const selected: string[] = [];
  for (const { name, body } of sections) {
    const headSlug = name.toLowerCase().split(/\s+/)[0] ?? '';
    const utility = headingToUtility[headSlug];
    if (utility && directLower.has(utility)) {
      selected.push(`### ${name}\n${body}`.replace(/\s+$/, ''));
    }
  }

  return (preamble + selected.join('\n\n')).trim();
}

function splitCover(core: string): [string, string] {
  const marker = 'Thank you for taking the time';
  const idx = core.indexOf(marker);
  if (idx === -1) {
    throw new Error('Could not find the start of the body intro in core_lease.md');
  }
  return [core.slice(0, idx).trim(), core.slice(idx).trim()];
}

function wrapIntro(text: string): string {
  const marker = '\n\n[[DYNAMIC_TABLE_OF_CONTENTS]]';
  const idx = text.indexOf(marker);
  if (idx === -1) return text;
  const intro = stripSourceComments(text.slice(0, idx)).trim();
  const rest = text.slice(idx + marker.length);
  return (
    '<div class="welcome">' +
    '<div class="welcome-eyebrow">A note before we begin</div>' +
    `<p class="welcome-body">${escapeHtml(intro)}</p>` +
    `</div>\n\n[[DYNAMIC_TABLE_OF_CONTENTS]]${rest}`
  );
}

function normalizeHeadingTitle(raw: string): string {
  return raw.replace(/^\d+\.\s*/, '').trim();
}

interface Section {
  number: number;
  title: string;
  toc_title: string;
  charge: boolean;
}

function renumberMainSections(text: string): {
  text: string;
  sections: Section[];
  headingMap: Record<string, number>;
} {
  const sections: Section[] = [];
  const headingMap: Record<string, number> = {};
  let current = 0;
  let inNumberedArea = false;
  const output: string[] = [];

  for (const line of text.split('\n')) {
    const match = line.match(/^##\s+(.+?)\s*$/);
    if (!match) {
      output.push(line);
      continue;
    }
    const rawTitle = (match[1] ?? '').trim();
    const title = normalizeHeadingTitle(rawTitle);
    const canonical = title.toUpperCase();

    if (canonical === 'AGREEMENT BETWEEN LANDLORD AND TENANT') {
      inNumberedArea = true;
      output.push(`## ${title}`);
      continue;
    }
    if (!inNumberedArea || UNNUMBERED_MAIN_HEADINGS.has(canonical)) {
      output.push(`## ${title}`);
      continue;
    }
    current += 1;
    // Charge is flagged in the TOC only — NOT in the body heading.
    output.push(`## <span class="num">${current}.</span> ${title}`);
    sections.push({
      number: current,
      title,
      toc_title: titleForToc(title),
      charge: CHARGE_HEADINGS.has(canonical),
    });
    headingMap[canonical] = current;
  }

  return { text: output.join('\n'), sections, headingMap };
}

function rewriteSubsectionPrefixes(text: string): string {
  let currentSection: string | null = null;
  const output: string[] = [];
  for (let line of text.split('\n')) {
    const sectionMatch = line.match(/^##\s+(?:<span class="num">)?(\d+)\.(?:<\/span>)?\s+.+$/);
    if (sectionMatch) {
      currentSection = sectionMatch[1] ?? null;
      output.push(line);
      continue;
    }
    if (currentSection) {
      line = line.replace(/^(\d+)\.(\d+)\./, `${currentSection}.$2.`);
    }
    output.push(line);
  }
  return output.join('\n');
}

function renderToc(sections: Section[]): string {
  const items = sections.map((s) => {
    // Charge dot only on charged rows, emitted INLINE at the end of .toc-name
    // (NOT a flex sibling — an empty flex-item dot makes WeasyPrint paint a
    // zero-width baseline strut that renders as a stray hairline in some viewers;
    // see .toc-charge in whp_lease.css). Mirror of render_lease.py render_toc.
    const chargeDot = s.charge ? '<span class="toc-charge"></span>' : '';
    return (
      `<li class="toc-item">` +
      `<span class="num">${s.number}.</span>` +
      `<span class="toc-name">${escapeHtml(s.toc_title)}${chargeDot}</span>` +
      '</li>'
    );
  });
  return [
    '<section class="toc-shell">',
    // The decorative rule is an inline-block .toc-rule span (NOT an empty bordered
    // ::after) — see .toc-title in whp_lease.css for why (WeasyPrint strut).
    '<div class="toc-title">Table of Contents<span class="toc-rule"></span></div>',
    '<div class="toc-subtitle"><em>Agreement Between Landlord and Tenant</em></div>',
    '<ol class="toc-list">',
    ...items,
    '</ol>',
    '<p class="toc-note"><span class="star-dot"></span>Indicates a section with specific up-front or potential charges during the lease term.</p>',
    '</section>',
  ].join('\n');
}

function replaceReferenceIds(text: string, headingMap: Record<string, number>): string {
  const utilityNumber = headingMap['UTILITY RESPONSIBILITY'];
  const outdoorNumber = headingMap['PATIOS / BALCONIES / DECKS'];

  // WEL-89: symbolic cross-refs to the deposit section and the carpet-fee
  // subsection, resolved from the FINAL numbering so they never drift. Mirror of
  // render_lease.py replace_reference_ids — keep in lock-step. The deposit
  // section is always present; the carpet fee is subsection N.1 of CARPET and
  // present only when carpet is. Used by the server-built payment-plan bullets.
  const securityNumber = headingMap['SECURITY / DAMAGE / CLEANING DEPOSIT'];
  if (securityNumber) {
    text = text.replace(/\[\[REF:security_deposit\]\]/g, `Section ${securityNumber}`);
  } else {
    text = text.replace(/\[\[REF:security_deposit\]\]/g, 'the Security Deposit section');
  }
  const carpetNumber = headingMap['CARPET'];
  if (carpetNumber) {
    text = text.replace(/\[\[REF:carpet_fee\]\]/g, `Section ${carpetNumber}.1`);
  } else {
    text = text.replace(/\[\[REF:carpet_fee\]\]/g, 'the carpet cleaning fee provision');
  }

  // Spec §22/§24: the ENTRY section ("ENTRY / INSPECTIONS / NOTICE") is
  // referenced by 37.4 and the Approved Animals kenneling clause; the RENT
  // section is referenced by the Payment Plan default clause (spec §27). Both
  // are always-present core sections, resolved from the FINAL numbering. Mirror
  // of render_lease.py replace_reference_ids — keep in lock-step.
  const entryNumber = headingMap['ENTRY / INSPECTIONS / NOTICE'];
  if (entryNumber) {
    text = text.replace(/\[\[REF:entry_section\]\]/g, `Section ${entryNumber}`);
  } else {
    text = text.replace(/\[\[REF:entry_section\]\]/g, 'the Entry and Inspections section');
  }
  const rentNumber = headingMap['RENT'];
  if (rentNumber) {
    text = text.replace(/\[\[REF:rent_section\]\]/g, `Section ${rentNumber}`);
  } else {
    text = text.replace(/\[\[REF:rent_section\]\]/g, 'the Rent section');
  }

  // Move-In Payment Plan cross-refs (docs/move-in-payment-plan-plan.md). Mirror
  // of render_lease.py replace_reference_ids — keep in lock-step.
  for (const [ref, heading, fallback] of [
    ['payment_plan', 'MOVE-IN PAYMENT PLAN', 'the Move-In Payment Plan section'],
    ['amounts_due', 'SUMMARY OF AMOUNTS DUE', 'the Summary of Amounts Due'],
    ['move_in_fees', 'MOVE-IN FEES', 'the Move-In Fees section'],
  ] as const) {
    const n = headingMap[heading];
    text = text.split(`[[REF:${ref}]]`).join(n ? `Section ${n}` : fallback);
  }

  if (utilityNumber) {
    text = text.replace(/\[\[REF:utility_responsibility\]\]/g, `Section ${utilityNumber}`);
  }
  if (outdoorNumber) {
    text = text.replace(/\[\[REF:outdoor_space\]\]/g, `Section ${outdoorNumber}`);
    // §23.1 storage clause: name the real Patios/Balconies/Decks section as the
    // exception when that section is present in the lease.
    text = text.replace(
      /\[\[OUTDOOR_SPACE_STORAGE_CLAUSE\]\]/g,
      `With the exception of Section ${outdoorNumber}, residents are not permitted to leave, store, or place any items outside of the Premises.`,
    );
  } else {
    text = text.replace(/\[\[REF:outdoor_space\]\]/g, 'this Agreement');
    // No Patios/Balconies/Decks section in this lease — drop the exception
    // clause entirely instead of dangling a reference.
    text = text.replace(
      /\[\[OUTDOOR_SPACE_STORAGE_CLAUSE\]\]/g,
      'Residents are not permitted to leave, store, or place any items outside of the Premises.',
    );
  }
  return text;
}

// ---------------------------------------------------------------------------
// DocuSeal tag expansion (resident-count-aware).
// Mirror of the Python helpers in lease_render/render_lease.py — keep in
// lock-step. See the registry in
// lease_components/docuseal_signer_field_registry.md for the named-role
// policy. DocuSeal tags are self-contained, so there is no setup section.
// ---------------------------------------------------------------------------

const RESIDENT_COUNT_MIN = 1;
const RESIDENT_COUNT_MAX = 12;

function clampResidentCount(value: unknown): number {
  let n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) n = RESIDENT_COUNT_MIN;
  n = Math.floor(n);
  if (n < RESIDENT_COUNT_MIN) return RESIDENT_COUNT_MIN;
  if (n > RESIDENT_COUNT_MAX) return RESIDENT_COUNT_MAX;
  return n;
}

function renderResidentInitials(residentCount: number): string {
  // WEL-42: one small initials field per resident in a fixed-width slot, sized
  // tiny + non-wrapping with an explicit width/height — mirrors render_lease.py
  // so the server PDF and the client preview stay in lock-step (see the
  // dual-renderer note). The compact slots replaced body-size tags that drew
  // ~1.9in-wide DocuSeal fields stacked one-per-line in the narrow column.
  const parts: string[] = [];
  for (let k = 1; k <= residentCount; k += 1) {
    parts.push(
      `<span class="itag-slot">{{Int${k};type=initials;role=Resident${k};width=13.2;height=6}}</span>`,
    );
  }
  return `<span class="initials-row">${parts.join('')}</span>`;
}

// One signature <td>: a small, no-wrap DocuSeal signature tag above its party
// label. The tag MUST stay intact on one line in the PDF text layer or DocuSeal
// can't parse it into a signature field. The SIGNATURES section is full-width
// (FULLWIDTH markers in core_lease.md), `.sigtag` renders the tag tiny + no-wrap,
// and the width/height attributes size the actual signature field in DocuSeal.
// Mirror of signature_cell in render_lease.py — keep byte-identical.
function signatureCell(
  fieldName: string,
  role: string,
  label: string,
  width = 200,
  height = 44,
  capacity = '',
): string {
  const tag = `{{${fieldName};type=signature;role=${role};width=${width};height=${height}}}`;
  // Optional entity signing-capacity sub-line BELOW the name line (e.g. Move-Out
  // Funds Lessor: "Manager, Welcome Home Properties LLC"). Empty for personal
  // signers, so their markup is unchanged. Mirror of signature_cell in render_lease.py.
  const cap = capacity ? `<span class="sigcapacity">${capacity}</span>` : '';
  return `<td><span class="sigtag">${tag}</span><br/><span class="siglabel">${label}</span>${cap}</td>`;
}

// Label for the k-th resident signature cell: the actual signer name (names is
// positional — index k-1 is the Resident{k} signer) else generic "Resident k".
// Naming a cell is cosmetic — no role / signing-order change. Mirror of
// _resident_sig_label in render_lease.py.
function residentSigLabel(names: string[] | undefined, k: number): string {
  const n = names && names[k - 1];
  return n && n.trim() ? n : `Resident ${k}`;
}

function renderSignatureBlock(residentCount: number, names?: string[]): string {
  const cells: string[] = [];
  for (let k = 1; k <= residentCount; k += 1) {
    cells.push(signatureCell(`Sig${k}`, `Resident${k}`, residentSigLabel(names, k)));
  }
  const residentRows: string[] = [];
  let i = 0;
  while (i < cells.length) {
    if (i + 1 < cells.length) {
      residentRows.push(`  <tr>\n    ${cells[i]}\n    ${cells[i + 1]}\n  </tr>`);
      i += 2;
    } else {
      residentRows.push(`  <tr>\n    ${cells[i]}\n    <td></td>\n  </tr>`);
      i += 1;
    }
  }
  // Lessor rows grouped in their own tbody so they stay together (mirror of
  // render_lease.py).
  const lessorRows = [
    `  <tr>\n    ${signatureCell('Broker', 'Broker', 'Lessor')}\n    <td></td>\n  </tr>`,
    `  <tr>\n    ${signatureCell('DesignatedBroker', 'Designated Broker', 'Designated Broker')}\n    <td></td>\n  </tr>`,
  ];
  return (
    '<table class="signatures" style="border: 0; border-collapse: collapse;">\n' +
    `<tbody>\n${residentRows.join('\n')}\n</tbody>\n` +
    `<tbody class="sig-lessor">\n${lessorRows.join('\n')}\n</tbody>\n` +
    '</table>'
  );
}

// Exhibit/addendum [[SIGNATURE_BLOCK]] (today: the Lead-Based Paint disclosure's
// Certification of Accuracy). Identical to renderSignatureBlock EXCEPT the Lessor
// side is the Broker row ONLY — the Designated Broker signs ONCE, on the MAIN
// lease (WEL-9). Wrapped in `.sig-keep` so the block travels to the next page as
// one unit instead of clipping off the bottom. Mirror of
// render_exhibit_signature_block in render_lease.py — keep byte-identical.
function renderExhibitSignatureBlock(residentCount: number, names?: string[]): string {
  const cells: string[] = [];
  for (let k = 1; k <= residentCount; k += 1) {
    cells.push(signatureCell(`Sig${k}`, `Resident${k}`, residentSigLabel(names, k)));
  }
  const residentRows: string[] = [];
  let i = 0;
  while (i < cells.length) {
    if (i + 1 < cells.length) {
      residentRows.push(`  <tr>\n    ${cells[i]}\n    ${cells[i + 1]}\n  </tr>`);
      i += 2;
    } else {
      residentRows.push(`  <tr>\n    ${cells[i]}\n    <td></td>\n  </tr>`);
      i += 1;
    }
  }
  // Lessor side: Broker ONLY (Designated Broker is main-lease-only).
  const lessorRows = [
    `  <tr>\n    ${signatureCell('Broker', 'Broker', 'Lessor')}\n    <td></td>\n  </tr>`,
  ];
  return (
    '<div class="sig-keep">\n' +
    '<table class="signatures" style="border: 0; border-collapse: collapse;">\n' +
    `<tbody>\n${residentRows.join('\n')}\n</tbody>\n` +
    `<tbody class="sig-lessor">\n${lessorRows.join('\n')}\n</tbody>\n` +
    '</table>\n' +
    '</div>'
  );
}

// `isExhibit` selects which signature block [[SIGNATURE_BLOCK]] expands to: the
// MAIN lease block (Broker + Designated Broker) or the EXHIBIT block (Broker
// only). Mirror of expand_signer_markers in render_lease.py.
function expandSignerMarkers(
  text: string,
  residentCount: number,
  names?: string[],
  isExhibit = false,
): string {
  const initials = renderResidentInitials(residentCount);
  const signatures = isExhibit
    ? renderExhibitSignatureBlock(residentCount, names)
    : renderSignatureBlock(residentCount, names);
  return text
    .split('[[RESIDENT_INITIALS]]').join(initials)
    .split('[[SIGNATURE_BLOCK]]').join(signatures);
}

// Lease Adoption Addendum signature block: residents (current + adoptees)
// plus a SINGLE Landlord — NO Broker / Designated Broker. Mirror of
// render_addendum_signature_block in render_lease.py — keep byte-identical.
function renderAddendumSignatureBlock(residentCount: number, names?: string[]): string {
  const cells: string[] = [];
  for (let k = 1; k <= residentCount; k += 1) {
    cells.push(signatureCell(`Sig${k}`, `Resident${k}`, residentSigLabel(names, k)));
  }
  const rows: string[] = [];
  let i = 0;
  while (i < cells.length) {
    if (i + 1 < cells.length) {
      rows.push(`  <tr>\n    ${cells[i]}\n    ${cells[i + 1]}\n  </tr>`);
      i += 2;
    } else {
      rows.push(`  <tr>\n    ${cells[i]}\n    <td></td>\n  </tr>`);
      i += 1;
    }
  }
  rows.push(
    `  <tr>\n    ${signatureCell('Landlord', 'Landlord', 'Lessor')}\n    <td></td>\n  </tr>`,
  );
  return (
    '<table class="signatures" style="border: 0; border-collapse: collapse;">\n' +
    rows.join('\n') +
    '\n</table>'
  );
}

// Build the Lease Adoption Addendum markdown. Renders ONLY the
// lease_adoption_addendum.md component (short standalone doc, NOT the full
// lease compile order). Sender prefill values are substituted as ordinary
// {{ variable }} placeholders; the signature block is expanded from the
// [[ADDENDUM_SIGNATURE_BLOCK]] marker. Mirror of build_addendum_markdown in
// render_lease.py.
function buildAddendumMarkdown(config: RenderConfig): string {
  let text = readComponent('lease_adoption_addendum.md');
  text = stripSourceComments(text);
  text = replaceVariables(text, config.variables);
  const residentCount = clampResidentCount(config.resident_count);
  text = text
    .split('[[ADDENDUM_SIGNATURE_BLOCK]]')
    .join(renderAddendumSignatureBlock(residentCount, config.resident_signers));
  return text.trim();
}

// Derive a DocuSeal FieldName from a role string. Roles in the move-out funds
// roster are already unique (Resident1.., Cosigner1..,
// Lessor), so the stripped-to-alnum role yields a unique, parseable field name.
// Mirror of _sanitize_field_name in render_lease.py — keep byte-identical output.
function sanitizeFieldName(role: string): string {
  const cleaned = String(role).replace(/[^A-Za-z0-9]+/g, '');
  return cleaned ? `Sig${cleaned}` : 'Sig';
}

// Move-Out Funds Direction Agreement [[MOVEOUT_FUNDS_SIGNATURE_BLOCK]] (WEL-48):
// every party signs in PARALLEL — one DocuSeal signature tag per entry in the
// `signers` roster (residents Resident{i}, co-signers Cosigner{i}, the
// and the single WHP Lessor). WEL-143: there is no DesignatedRecipient signer —
// only holders of rights in the deposit sign, and the recipient holds none.
// Two-column cells (signature tag above the signer's display name), paired
// two-per-row — same visual treatment as the addendum. No Broker / Designated
// Broker split, no sequential countersign. Mirror of
// render_moveout_funds_signature_block in render_lease.py — keep byte-identical.
function renderMoveoutFundsSignatureBlock(signers: MoveoutFundsSigner[]): string {
  const cells: string[] = [];
  for (const s of signers ?? []) {
    const role = String(s?.role ?? '').trim();
    if (!role) continue;
    const label = (String(s?.name ?? '').trim() || role);
    // WHP signs the LLC (Lessor) in a representative capacity — render a capacity
    // sub-line UNDER the Lessor name (entity-signature format). Mirror of the
    // render_lease.py special case; residents / co-signers / recipient sign
    // personally (no capacity).
    const capacity = role === 'Lessor' ? 'Manager, Welcome Home Properties LLC' : '';
    cells.push(signatureCell(sanitizeFieldName(role), role, label, 200, 44, capacity));
  }
  const rows: string[] = [];
  let i = 0;
  while (i < cells.length) {
    if (i + 1 < cells.length) {
      rows.push(`  <tr>\n    ${cells[i]}\n    ${cells[i + 1]}\n  </tr>`);
      i += 2;
    } else {
      rows.push(`  <tr>\n    ${cells[i]}\n    <td></td>\n  </tr>`);
      i += 1;
    }
  }
  return (
    '<table class="signatures" style="border: 0; border-collapse: collapse;">\n' +
    rows.join('\n') +
    '\n</table>'
  );
}

// Backward-compat defaults for the co-signer recital / bound-party variables
// (WEL-48 review edits) — mirror of MOVEOUT_COSIGNER_DEFAULTS in render_lease.py.
// A config STORED before these variables existed (a quiet-swap re-render of an
// earlier submission) omits them; default to the inert no-co-signer prose so no
// {{ }} placeholder is left unresolved. New sends always supply real values.
const MOVEOUT_COSIGNER_DEFAULTS: Record<string, string> = {
  cosigners_recital: '',
  bound_parties_and: 'Residents and the Designated Recipient',
  bound_parties_or: 'Residents or the Designated Recipient',
};

// Build the Move-Out Funds Direction Agreement markdown (WEL-48). Renders ONLY
// the moveout_funds_direction.md component (short standalone doc, NOT the full
// lease compile order). Every value is substituted as an ordinary {{ variable }}
// placeholder; the parallel signature block is expanded from the
// [[MOVEOUT_FUNDS_SIGNATURE_BLOCK]] marker driven by config.signers. Mirror of
// build_moveout_funds_markdown in render_lease.py.
function buildMoveoutFundsMarkdown(config: RenderConfig): string {
  let text = readComponent('moveout_funds_direction.md');
  text = stripSourceComments(text);
  text = replaceVariables(text, { ...MOVEOUT_COSIGNER_DEFAULTS, ...config.variables });
  text = text
    .split('[[MOVEOUT_FUNDS_SIGNATURE_BLOCK]]')
    .join(renderMoveoutFundsSignatureBlock(config.signers ?? []));
  return text.trim();
}

// Standalone Security Deposit Payment Plan [[PAYMENT_PLAN_SIGNATURE_BLOCK]]
// (WEL-77): the residents (roles Resident{i}, order 0 — sign first) then the
// single WHP Lessor (role Lessor, order 1 — countersigns), exactly the roster
// built by buildPaymentPlanSubmitters in paymentPlanDoc.ts. The signing-order
// split is enforced by DocuSeal from the submitter `order` field; this block
// just lays out one signature tag per signer in roster order. Two-column cells
// (signature tag above the signer's display name), paired two-per-row — same
// visual treatment as the move-out funds / addendum blocks. Mirror of
// render_payment_plan_signature_block in render_lease.py — keep byte-identical.
function renderPaymentPlanSignatureBlock(signers: MoveoutFundsSigner[]): string {
  const cells: string[] = [];
  for (const s of signers ?? []) {
    const role = String(s?.role ?? '').trim();
    if (!role) continue;
    const label = (String(s?.name ?? '').trim() || role);
    cells.push(signatureCell(sanitizeFieldName(role), role, label));
  }
  const rows: string[] = [];
  let i = 0;
  while (i < cells.length) {
    if (i + 1 < cells.length) {
      rows.push(`  <tr>\n    ${cells[i]}\n    ${cells[i + 1]}\n  </tr>`);
      i += 2;
    } else {
      rows.push(`  <tr>\n    ${cells[i]}\n    <td></td>\n  </tr>`);
      i += 1;
    }
  }
  return (
    '<table class="signatures" style="border: 0; border-collapse: collapse;">\n' +
    rows.join('\n') +
    '\n</table>'
  );
}

// Build the standalone Security Deposit Payment Plan markdown (WEL-77). Renders
// ONLY the payment_plan_standalone.md component (short standalone doc, NOT the
// full lease compile order, and distinct from the in-lease payment_plan.md).
// Every value is substituted as an ordinary {{ variable }} placeholder; the
// residents-then-Lessor signature block is expanded from the
// [[PAYMENT_PLAN_SIGNATURE_BLOCK]] marker driven by config.signers. Mirror of
// build_payment_plan_markdown in render_lease.py.
function buildPaymentPlanMarkdown(config: RenderConfig): string {
  let text = readComponent('payment_plan_standalone.md');
  text = stripSourceComments(text);
  text = replaceVariables(text, config.variables);
  text = text
    .split('[[PAYMENT_PLAN_SIGNATURE_BLOCK]]')
    .join(renderPaymentPlanSignatureBlock(config.signers ?? []));
  return text.trim();
}

function buildMarkdown(config: RenderConfig): { main: string; exhibitParts: string[] } {
  const [, bodyAfterSplit] = splitCover(readComponent('core_lease.md'));
  let body = wrapIntro(bodyAfterSplit);
  body = removeGeneratedTocNote(body);

  const modules = config.modules;
  let animalModule = readComponent(modules.animal_policy as string);
  if (modules.animal_policy === 'animals_permitted.md') {
    animalModule = demoteH2AfterFirst(animalModule);
  }

  const mfrSubtype = modules.mfr_subtype;
  const replacements: Record<string, string> = {
    '[[ANIMAL_POLICY_MODULE]]': animalModule,
    // WEL-89: CARPET is the LAST numbered body section (placed in core_lease.md
    // AFTER the also-numbered payment plan), included only when the unit has
    // carpet. Gate on config.has_carpet (default true via !== false → an ABSENT
    // key includes it, so every pre-WEL-89 config renders carpet unchanged). When
    // false the placeholder collapses to '' and the section + its number drop;
    // because carpet is last, only the unreferenced SUMMARY number shifts — the
    // PAYMENT PLAN number stays put. Mirror of render_lease.py build_markdown.
    '[[CARPET_MODULE]]': config.has_carpet !== false ? readComponent('carpet.md') : '',
    '[[PARKING_MODULE]]': readComponent(modules.parking as string),
    '[[PROPERTY_TYPE_MODULE]]': readComponent(modules.property_type as string),
    '[[MFR_SUBTYPE_MODULE]]': mfrSubtype ? readComponent(mfrSubtype) : '',
    '[[OUTDOOR_SPACE_MODULE]]': config.include_outdoor_space ? readComponent('outdoor_space.md') : '',
    '[[HOA_MODULE]]': config.include_hoa ? readComponent('hoa.md') : '',
    '[[SFR_AREAS_NOT_INCLUDED_MODULE]]': config.include_sfr_areas_not_included
      ? readComponent('sfr_areas_not_included.md')
      : '',
    // §4 negotiated no-fee early-termination-by-notice option (staff-enabled).
    // Slots into §4 after the fee buyout, before Holdover; the earliest permitted
    // termination date renders via {{ early_termination_earliest_date }}.
    '[[EARLY_TERMINATION_NOTICE_MODULE]]': config.include_early_termination_notice
      ? readComponent('early_termination_notice.md')
      : '',
    '[[UTILITY_RESPONSIBILITY_MODULE]]': renderUtilityFramework(config),
    // RENEWAL policy: renewal leases omit the YARD CARE section. buildRenderConfig
    // sets include_yard_care:false AND blanks modules.yard_care for renewals; gate
    // on the flag (default true via !== false) so every other lease is unchanged.
    '[[YARD_CARE_MODULE]]':
      config.include_yard_care !== false && modules.yard_care
        ? readComponent(modules.yard_care as string)
        : '',
    // SUMMARY OF AMOUNTS DUE only — it is wrapped with the signatures in the
    // CLOSING block so the two always share a page (highest-priority rule). The
    // payment-plan schedule renders separately, ABOVE that block, via
    // [[PAYMENT_PLAN_MODULE]] (mirrors render_lease.py build_markdown).
    '[[FINANCIAL_SUMMARY_MODULE]]': readComponent('financial_summary.md'),
    '[[PAYMENT_PLAN_MODULE]]': config.include_payment_plan
      ? readComponent('payment_plan.md')
      : '',
    // MOVE-IN FEES: staff-added one-time fees, only when the lease has one.
    // Mirrors render_lease.py build_markdown.
    '[[MOVE_IN_FEES_MODULE]]': config.include_move_in_fees
      ? readComponent('move_in_fees.md')
      : '',
  };

  for (const [placeholder, value] of Object.entries(replacements)) {
    body = body.split(placeholder).join(value);
  }

  if (!body.includes('[[EXHIBITS_AND_INSTRUCTION_SHEETS]]')) {
    throw new Error('Core lease missing [[EXHIBITS_AND_INSTRUCTION_SHEETS]] placeholder');
  }

  const splitIdx = body.indexOf('[[EXHIBITS_AND_INSTRUCTION_SHEETS]]');
  let main = body.slice(0, splitIdx);
  let tail = body.slice(splitIdx + '[[EXHIBITS_AND_INSTRUCTION_SHEETS]]'.length);
  tail = tail.replace(/\[\[LEAD_BASED_PAINT_DISCLOSURE_MODULE\]\]/g, '');

  assertPendingDecisionSlotPlaced(main, config.variables);
  main = replaceVariables(main, config.variables);
  main = stripSourceComments(main);
  const renumbered = renumberMainSections(main);
  main = renumbered.text;
  main = rewriteSubsectionPrefixes(main);
  main = replaceReferenceIds(main, renumbered.headingMap);
  main = main.replace('[[DYNAMIC_TABLE_OF_CONTENTS]]', renderToc(renumbered.sections));

  // Each exhibit/instruction sheet is its own page-broken section (wrapped in
  // renderLeaseHtmlParts), mirroring render_lease.py — see that file for why
  // (forced page breaks don't work inside the main 2-column flow).
  const exhibitSources = [
    stripSourceComments(readComponent('exhibit_wear_and_tear.md')),
    stripSourceComments(readComponent('exhibit_mold_moisture.md')),
  ];
  const directInstructions = renderDirectUtilityInstructions(config);
  if (directInstructions) exhibitSources.push(stripSourceComments(directInstructions));
  if (config.include_lbp) {
    exhibitSources.push(stripSourceComments(readComponent('lead_based_paint_disclosure.md')));
  }

  // Resident-count-aware marker expansion. See render_lease.py's
  // expand_signer_markers for the matching server-side helper.
  const residentCount = clampResidentCount(config.resident_count);
  const signerNames = config.resident_signers;
  const mainOut = expandSignerMarkers(main.trim(), residentCount, signerNames);
  const exhibitParts = exhibitSources.map((part) =>
    expandSignerMarkers(replaceVariables(part, config.variables), residentCount, signerNames, true),
  );
  return { main: mainOut, exhibitParts };
}

// Cover page for the Lease Adoption Addendum — the agreement-suite cover
// treatment (full-width photo band, brand line + tracked doc label, large-serif
// recital with underlined .key values, bottom identity rail), matching the
// Leaseholder Change / Removal agreement covers so the addendum reads as part
// of the same document suite. Byte-for-byte mirror of render_addendum_cover in
// render_lease.py (dual-renderer parity).
function renderAddendumCover(config: RenderConfig, watermarkDiv = ''): string {
  const variables = config.variables as Record<string, string>;
  const key = (name: string): string => {
    const value = variables[name] ?? `{{ ${name} }}`;
    return `<span class="key">${escapeHtml(String(value))}</span>`;
  };
  const summary =
    `This addendum adds ${key('new_residents')} to the lease held by ` +
    `${key('current_residents')} for the home at ${key('premises')}, ` +
    `under the same terms through ${key('end_date')}.`;
  return `
<div class="cover">
  ${watermarkDiv}<img class="cover-photo" src="assets/cover-photo.png" alt="A warm, sunlit home entryway" />
  <div class="cover-top">
    <span class="cover-brand">Welcome Home Properties</span>
    <span class="cover-doclabel">LEASE ADOPTION ADDENDUM</span>
  </div>
  <div class="cover-mid">
    <p class="cover-summary">${summary}</p>
  </div>
  <div class="cover-rail">
    <span class="rail-left">PROPERTY MANAGEMENT &nbsp;&middot;&nbsp; WALLA WALLA, WA</span>
    <img class="rail-mono" src="assets/whp-monogram-brass.png" alt="Welcome Home Properties" />
  </div>
</div>
`.trim();
}

// Shared agreement-suite cover shell for the short standalone signable
// documents (Move-Out Funds Direction, Security Deposit Payment Plan) —
// byte-for-byte the SAME structure/classes as renderAddendumCover (which stays
// self-contained so the addendum's golden is untouched). docLabel and summary
// are the only per-document pieces and must arrive HTML-escaped/-safe.
// Byte-for-byte mirror of render_agreement_cover in render_lease.py.
function renderAgreementCover(docLabel: string, summary: string, watermarkDiv = ''): string {
  return `
<div class="cover">
  ${watermarkDiv}<img class="cover-photo" src="assets/cover-photo.png" alt="A warm, sunlit home entryway" />
  <div class="cover-top">
    <span class="cover-brand">Welcome Home Properties</span>
    <span class="cover-doclabel">${docLabel}</span>
  </div>
  <div class="cover-mid">
    <p class="cover-summary">${summary}</p>
  </div>
  <div class="cover-rail">
    <span class="rail-left">PROPERTY MANAGEMENT &nbsp;&middot;&nbsp; WALLA WALLA, WA</span>
    <img class="rail-mono" src="assets/whp-monogram-brass.png" alt="Welcome Home Properties" />
  </div>
</div>
`.trim();
}

// A recital key value for an agreement cover: the underlined bold
// `<span class="key">` treatment (same as renderAddendumCover's inline helper).
// Mirror of _cover_key in render_lease.py.
function coverKey(variables: Record<string, string>, name: string): string {
  const value = variables[name] ?? `{{ ${name} }}`;
  return `<span class="key">${escapeHtml(String(value))}</span>`;
}

// Cover page for the Move-Out Funds Direction Agreement — the agreement-suite
// cover treatment, replacing the lighter standalone masthead. Byte-for-byte
// mirror of render_moveout_funds_cover in render_lease.py.
function renderMoveoutFundsCover(config: RenderConfig, watermarkDiv = ''): string {
  const variables = config.variables as Record<string, string>;
  const summary =
    `This agreement directs the remaining funds from the lease held by ` +
    `${coverKey(variables, 'residents')} at ${coverKey(variables, 'premises')} ` +
    `to ${coverKey(variables, 'designated_recipient_name')}.`;
  return renderAgreementCover('MOVE-OUT FUNDS DIRECTION', summary, watermarkDiv);
}

// Cover page for the standalone payment plan — the agreement-suite cover
// treatment with the TYPE-ADAPTIVE plan name as the doc label ("SECURITY
// DEPOSIT PAYMENT PLAN" / "RENT PAYMENT PLAN" / …) and the matching
// plan_amounts_label in the recital. Byte-for-byte mirror of
// render_payment_plan_cover in render_lease.py.
function renderPaymentPlanCover(config: RenderConfig, watermarkDiv = ''): string {
  const variables = config.variables as Record<string, string>;
  const planName = String(variables.plan_name ?? 'Security Deposit Payment Plan');
  const docLabel = escapeHtml(planName.toUpperCase());
  const amountsLabel = escapeHtml(String(variables.plan_amounts_label ?? 'move-in amounts'));
  const summary =
    `This plan sets the schedule by which ${coverKey(variables, 'residents')} ` +
    `will pay ${coverKey(variables, 'plan_total')} in ${amountsLabel} ` +
    `for the home at ${coverKey(variables, 'premises')}.`;
  return renderAgreementCover(docLabel, summary, watermarkDiv);
}

// Cover (design spec): full-bleed property photo above a bespoke narrative
// sentence (residents/address/dates as brass-underwashed .var spans), closed by
// the brand lockup seal + tagline. Mirror of render_cover in render_lease.py.
function renderCover(config: RenderConfig, watermarkDiv = ''): string {
  const variables = config.variables as Record<string, string>;
  const residentNames = escapeHtml(variables.resident_names ?? '{{ resident_names }}');
  const premisesAddress = escapeHtml(variables.premises_address ?? '{{ premises_address }}');
  const start = escapeHtml(variables.lease_start_date ?? '{{ lease_start_date }}');
  const end = escapeHtml(variables.lease_end_date ?? '{{ lease_end_date }}');

  return `
<section class="cover-page">
  ${watermarkDiv}<div class="cover-hero">
    <img class="hero-photo" src="assets/cover-photo.png" alt="" />
  </div>
  <div class="cover-narrative">
    <div class="cover-eyebrow-row">
      <div class="cover-welcome">Welcome home.</div>
      <div class="cover-doctype">Residential Lease Agreement</div>
    </div>
    <p class="cover-prose">
      This agreement is between
      <span class="var">Welcome Home Properties</span>
      and
      <span class="var wrap-name">${residentNames}</span>,
      for the home at
      <span class="var wrap-name">${premisesAddress}</span>,
      from
      <span class="var tabular">${start}</span>
      through
      <span class="var tabular">${end}</span>.
    </p>
    <div class="cover-signoff">
      <div class="seal">
        <img class="seal-lockup" src="assets/lockup.svg" alt="Welcome Home Properties" />
        <div class="seal-sub">Property management &middot; Walla Walla, WA</div>
      </div>
      <div class="tagline">Your property, handled.</div>
    </div>
  </div>
</section>
`.trim();
}

// Soft placeholder guard: preview must remain usable while the form is being
// filled out, so unresolved {{ var }} are highlighted in place instead of
// throwing. The Python renderer still throws hard for the signed PDF.
function softGuardPlaceholders(text: string): string {
  const keys = new Set<string>();
  const replaced = text.replace(PLACEHOLDER_RE, (match, key: string) => {
    keys.add(key);
    return `<span style="background:#fef3c7;color:#92400e;padding:0 4px;border-radius:2px" title="unresolved">${escapeHtml(match)}</span>`;
  });
  if (keys.size > 0) {
    console.warn('[leaseRenderer] unresolved placeholders:', [...keys].sort());
  }
  return replaced;
}

export interface LeaseHtmlParts {
  head: string;
  body: string;
  title: string;
}

export function renderLeaseHtmlParts(config: RenderConfig): LeaseHtmlParts {
  // Render-mode dispatch. Mirrors build_html's document_type branch in
  // render_lease.py. The 'adoption_addendum' mode renders ONLY the addendum
  // component (no lease cover page, residents + single Landlord signatures).
  const docType = String(config.document_type ?? 'lease');

  // B2 (Codex): guard staff-entered prefill (lease + addendum) against field
  // injection before any substitution.
  assertNoPrefillTagInjection(config.variables);

  const initials = String(config.watermark_initials ?? '');
  const isGenerated = Boolean(config.is_generated_for_signing);
  // Every document now opens with a cover (lease, addendum, move-out funds,
  // payment plan), whose zero-margin `@page cover` suppresses the
  // watermark.css `@page :first` margin box — so the staff initials render as
  // a VISIBLE element positioned inside the cover, in full on page 1 only,
  // never clipped by the cover's zero-margin page edge (WEL-13). Mirror of
  // render_lease.py build_html / build_addendum_html /
  // build_moveout_funds_html / build_payment_plan_html.
  const coverInitialsDiv =
    isGenerated && initials
      ? `<div class="cover-initials" style="string-set: user-initials '${escapeHtml(initials)}'">${escapeHtml(initials)}</div>\n`
      : '';

  if (docType === 'adoption_addendum') {
    const bodyMd = buildAddendumMarkdown(config);
    const bodyHtml = marked.parse(bodyMd, { async: false, gfm: true }) as string;
    const title = escapeHtml(config.title ?? 'Lease Adoption Addendum');
    const head = `<meta charset="utf-8" />\n<title>${title}</title>\n<link rel="stylesheet" href="https://use.typekit.net/oma6xts.css" />\n<style>${getLeaseCss()}</style>`;
    // Agreement-suite COVER PAGE (photo band + brand row + doc label + recital
    // + identity rail — the Leaseholder Change / Removal treatment), replacing
    // the earlier standalone masthead; the cover carries the brand + document
    // title, so the body carries no title of its own. The staff initials render
    // as the VISIBLE `.cover-initials` element inside the cover (the addendum's
    // first page is now the zero-margin `@page cover`, which suppresses the
    // watermark.css `:first` margin box) — exactly the full-lease WEL-13
    // treatment. The body then renders single-column (centered measure) via the
    // .doc-standalone class, same as the move-out/payment-plan siblings.
    // Mirrors render_lease.py build_addendum_html (dual-renderer parity).
    const cover = renderAddendumCover(config, coverInitialsDiv);
    const bodyInner = `${cover}\n<main class="lease-body doc-standalone adoption-addendum-doc">\n${bodyHtml}\n</main>`;
    return {
      head,
      body: softGuardPlaceholders(bodyInner),
      title,
    };
  }

  // WEL-48: the Move-Out Funds Direction Agreement renders ONLY its own
  // component (short standalone doc, parallel signature block from `signers`).
  if (docType === 'moveout_funds_direction') {
    const bodyMd = buildMoveoutFundsMarkdown(config);
    const bodyHtml = marked.parse(bodyMd, { async: false, gfm: true }) as string;
    const title = escapeHtml(config.title ?? 'Move-Out Funds Direction Agreement');
    const head = `<meta charset="utf-8" />\n<title>${title}</title>\n<link rel="stylesheet" href="https://use.typekit.net/oma6xts.css" />\n<style>${getLeaseCss()}</style>`;
    // Agreement-suite COVER PAGE (photo band + brand row + doc label + recital
    // + identity rail), replacing the earlier standalone masthead; the cover
    // carries the brand + document title, so the body carries no title of its
    // own. The body renders single-column (centered measure) via the
    // .doc-standalone class, so the full-width signature table is not clipped
    // by the lease's two-column flow. Mirrors render_lease.py
    // build_moveout_funds_html (dual-renderer parity).
    const cover = renderMoveoutFundsCover(config, coverInitialsDiv);
    const bodyInner = `${cover}\n<main class="lease-body doc-standalone moveout-funds-doc">\n${bodyHtml}\n</main>`;
    return {
      head,
      body: softGuardPlaceholders(bodyInner),
      title,
    };
  }

  // WEL-77: the standalone Security Deposit Payment Plan renders ONLY its own
  // component (short standalone doc, residents-then-Lessor signature block from
  // `signers`).
  if (docType === 'payment_plan') {
    const bodyMd = buildPaymentPlanMarkdown(config);
    const bodyHtml = marked.parse(bodyMd, { async: false, gfm: true }) as string;
    // Tag the (single) schedule table so the payment-schedule CSS applies. JS
    // replace hits the FIRST occurrence only (matching Python's count=1); the
    // signature block is `<table class="signatures">` and won't match.
    const bodyHtmlTagged = bodyHtml.replace('<table>', '<table class="payment-schedule">');
    const title = escapeHtml(config.title ?? 'Security Deposit Payment Plan');
    const head = `<meta charset="utf-8" />\n<title>${title}</title>\n<link rel="stylesheet" href="https://use.typekit.net/oma6xts.css" />\n<style>${getLeaseCss()}</style>`;
    // Agreement-suite COVER PAGE (type-adaptive doc label + recital), replacing
    // the earlier standalone masthead; the cover carries the brand + document
    // title, so the body carries no title of its own. The body renders
    // single-column via the .payment-plan-doc class (no inline column-count).
    // Mirrors render_lease.py build_payment_plan_html (dual-renderer parity).
    const cover = renderPaymentPlanCover(config, coverInitialsDiv);
    const bodyInner = `${cover}\n<main class="lease-body doc-standalone payment-plan-doc">\n${bodyHtmlTagged}\n</main>`;
    return {
      head,
      body: softGuardPlaceholders(bodyInner),
      title,
    };
  }

  // B1 (Codex): an unrecognized document_type must NOT silently fall through
  // to the full-lease path. Absent / 'lease' is the only accepted full-lease
  // value; anything else is a hard error (mirrors render_lease.py build_html).
  if (docType !== 'lease') {
    throw new Error(`Unknown document_type: ${JSON.stringify(docType)}`);
  }

  const { main: mainMd, exhibitParts } = buildMarkdown(config);
  let mainHtml = marked.parse(mainMd, { async: false, gfm: true }) as string;
  // Tag NUMBERED section headings so only they get the outdent brass numeral;
  // unnumbered headings (SIGNATURES, RESIDENT DESIGNATION, exhibit titles) stay
  // flush-left. Mirror of render_lease.py.
  mainHtml = mainHtml.replace(
    /<h2>(\s*<span class="num">)/g,
    '<h2 class="numbered">$1',
  );
  // Full-width breakout (approved-animals table) — mirror render_lease.py:
  // close the 2-column flow, emit a single-column full-width block, reopen.
  const hasFullwidth =
    mainHtml.includes('<p>[[FULLWIDTH_START]]</p>') ||
    mainHtml.includes('<p>[[CLOSING_START]]</p>');
  mainHtml = mainHtml
    .replace('<p>[[FULLWIDTH_START]]</p>', '</main>\n<div class="lease-body fullwidth-block">')
    .replace('<p>[[FULLWIDTH_END]]</p>', '</div>\n<main class="lease-body">');
  // CLOSING block = SUMMARY OF AMOUNTS DUE (balanced sub-block) + full-width
  // signatures. In the PDF render_document forces it onto a fresh page and may
  // also pull RESIDENT DESIGNATION into the balanced block; the preview is a
  // single scrolling document, so the classes are cosmetic but kept for parity.
  mainHtml = mainHtml
    .replace('<p>[[CLOSING_START]]</p>', '</main>\n<div class="lease-body closing-block">\n<div class="closing-balanced">')
    .replace('<p>[[BALANCED_END]]</p>', '</div>')
    .replace('<p>[[CLOSING_END]]</p>', '</div>\n<main class="lease-body">');
  // RESIDENT DESIGNATION wrapper. The preview is a single scrolling document
  // (not paginated); it uses the natural-flow `.designation` wrapper (the
  // renderer no longer force-paginates the designation).
  mainHtml = mainHtml
    .replace('<p>[[DESIGNATION_START]]</p>', '<div class="designation">')
    .replace('<p>[[DESIGNATION_END]]</p>', '</div>');
  const exhibitHtmls = exhibitParts.map(
    (part) => marked.parse(part, { async: false, gfm: true }) as string,
  );
  // `balance-tail`: the first block ends at the breakout, so balance its tail
  // page to fill both columns (else the right column above the table is empty).
  const firstMainClass = hasFullwidth ? 'lease-body balance-tail' : 'lease-body';
  const sectionParts = [`<main class="${firstMainClass}">\n${mainHtml}\n</main>`];
  for (let exhibitHtml of exhibitHtmls) {
    let sectionClass = 'lease-body exhibit';
    // Wear & Tear vs Damages: [[COMPARE_*]] markers → side-by-side sage/clay
    // comparison; the host exhibit goes single-column. Mirror of render_lease.py.
    if (exhibitHtml.includes('[[COMPARE_START]]')) {
      sectionClass += ' compare';
      exhibitHtml = exhibitHtml
        .replace(
          '<p>[[COMPARE_START]]</p>',
          '<div class="compare-grid"><div class="compare-col wear">',
        )
        .replace(
          '<p>[[COMPARE_SPLIT]]</p>',
          '</div><div class="compare-col damage">',
        )
        .replace('<p>[[COMPARE_END]]</p>', '</div></div>');
    }
    sectionParts.push(`<section class="${sectionClass}">\n${exhibitHtml}\n</section>`);
  }
  const bodyHtml = sectionParts.join('\n');
  const title = escapeHtml(config.title ?? 'Lease Agreement');

  // DocuSeal field tags are self-contained inline tokens emitted in place by
  // the marker expansion and component markdown; there is no separate
  // declaration/setup block to append (unlike the legacy Adobe Sign path).
  const head = `<meta charset="utf-8" />\n<title>${title}</title>\n<link rel="stylesheet" href="https://use.typekit.net/oma6xts.css" />\n<style>${getLeaseCss()}</style>`;
  const bodyInner = `${renderCover(config, coverInitialsDiv)}\n${bodyHtml}`;

  return {
    head,
    body: bundlePreviewAssets(softGuardPlaceholders(bodyInner)),
    title,
  };
}

export function renderLeaseHtml(config: RenderConfig): string {
  const parts = renderLeaseHtmlParts(config);
  return `<!doctype html>
<html>
<head>
${parts.head}
</head>
<body>
${parts.body}
</body>
</html>
`;
}
